<script>
import { required, maxLength } from "@vuelidate/validators";
import { tel } from "@/utils/form-validators";

import ChoixCommune from "@/containers/adresses-lad/choix-commune";
import Form from "@/components/molecules/form";
import FormRadio from "@/components/molecules/form-radio";
import FormInput from "@/components/molecules/form-input";
import FormCheckbox from "@/components/molecules/form-checkbox";
import FormSelect from "@/components/molecules/form-select";
import FormTip from "@/components/molecules/form-tip";
import Button from "@/components/atoms/button";
import Alert from "@/components/molecules/alert";
import FormFieldset from "@/components/molecules/form-fieldset";
import FormLegend from "@/components/molecules/form-fieldset/legend";
import FormGroup from "@/components/molecules/form-group";
import {
  formValidationProps,
  useFormValidation
} from "@/components/organisms/mixins/form-validation.mixin";
import { states } from "@/components/molecules/mixins/form.mixin";
import MentionsLegalesCompte from "@/components/molecules/mentions-legales/compte";
import DropDown from "@/components/molecules/drop-down";
import DropDownItem from "@/components/molecules/drop-down/drop-down-item";

export default {
  name: "AddressLadForm",
  components: {
    Form,
    FormRadio,
    FormInput,
    FormCheckbox,
    FormSelect,
    FormTip,
    Button,
    Alert,
    FormFieldset,
    FormLegend,
    FormGroup,
    ChoixCommune,
    MentionsLegalesCompte,
    DropDown,
    DropDownItem
  },
  props: {
    checkEligibilite: {
      type: Boolean,
      required: false,
      default: true
    },
    adresse: {
      type: Object,
      required: false,
      default: null
    },
    context: {
      type: String,
      required: false,
      default: "tunnel"
    },
    adresseAutosuggestIsLoading: {
      type: Boolean,
      required: false,
      default: false
    },
    complementAutosuggestIsLoading: {
      type: Boolean,
      required: false,
      default: null
    },
    suggestionsAdresse: {
      type: Array,
      required: false,
      default: () => []
    },
    suggestionsComplement: {
      type: Array,
      required: false,
      default: () => []
    },
    ...formValidationProps()
  },
  emits: [
    "communeValidated",
    "getSuggestionsAdresse",
    "getComplementsAdresse",
    "getCommunesByCodePostal",
    "save",
    "cancel"
  ],
  setup(props) {
    const { v$, getState, getErrorMessage, checkValidation, states } =
      useFormValidation(props);
    return {
      v$,
      getState,
      getErrorMessage,
      checkValidation,
      states
    };
  },
  data() {
    return {
      model: {
        nom: "",
        prenom: "",
        civilite: "",
        tel: "",
        code_insee: "",
        code_postal: "",
        nom_commune: "",
        adresse: "",
        complement_adresse: "",
        pays: "France",
        type_habitation: "maison",
        etage: "0",
        ascenseur: true,
        appartement: "",
        digicode1: "",
        digicode2: "",
        interphone: true,
        info_livreur: "",
        defaut: false,
        libelle: "",
        centre_ville: false,
        voie_sans_issue: false,
        sens_unique: false,
        rue_pietonne: false,
        access_semi_remorque: false,
        residence: false,
        normalisee: false,
        normalisation_proposee: false
      },
      etageOptions: [
        { text: "Etage*", value: "" },
        { text: "RDC", value: "0" },
        { text: "1er étage", value: "1" },
        { text: "2e étage", value: "2" },
        { text: "3e étage", value: "3" },
        { text: "4e étage", value: "4" },
        { text: "5e étage", value: "5" },
        { text: "6e étage", value: "6" },
        { text: "7e étage", value: "7" },
        { text: "8e étage", value: "8" },
        { text: "9e étage", value: "9" },
        { text: "10e étage", value: "10" },
        { text: "11e étage", value: "11" },
        { text: "12e étage", value: "12" },
        { text: "13e étage", value: "13" },
        { text: "14e étage", value: "14" },
        { text: "15e étage", value: "15" },
        { text: "16e étage", value: "16" },
        { text: "17e étage", value: "17" },
        { text: "18e étage", value: "18" },
        { text: "19e étage", value: "19" },
        { text: "20e étage et plus", value: "20" }
      ],
      typeHabitation: "maison",
      commune: null,
      adresseKey: "",
      complementKey: "",
      debounceTime: null
    };
  },
  validations() {
    return {
      model: {
        nom: {
          required
        },
        prenom: {
          required
        },
        civilite: {
          required
        },
        tel: {
          required,
          tel
        },
        etage: {
          /* eslint-disable */
          required: function () {
            return true;
          },
          requiredIfImmeuble: function () {
            if (this.model.type_habitation !== "maison") {
              return this.model.etage !== "";
            }
            return true;
          }
          /* eslint-enable */
        },
        adresse: {
          required
        },
        libelle: {
          required
        },
        digicode1: {
          maxLength: maxLength(15)
        },
        digicode2: {
          maxLength: maxLength(15)
        }
      }
    };
  },
  computed: {
    digicodeState() {
      if (
        this.getState(this.v$, "digicode1", "model") === states.ERROR ||
        this.getState(this.v$, "digicode2", "model") === states.ERROR
      ) {
        return states.ERROR;
      }
      return (
        this.getState(this.v$, "digicode1", "model") ||
        this.getState(this.v$, "digicode2", "model")
      );
    },
    digicodeError() {
      if (this.getErrorMessage("digicode1")) {
        return this.getErrorMessage("digicode1");
      }
      return this.getErrorMessage(
        "digicode2",
        "Le digicode saisi n'est pas valide"
      );
    }
  },
  mounted() {
    if (this.adresse) {
      this.hydrateModel(this.adresse);
    }
  },
  methods: {
    communeValidated(commune) {
      this.model.nom_commune = commune.nom_commune;
      this.model.code_insee = commune.code_insee;
      this.model.code_postal = commune.code_postal;
      this.commune = commune;
      this.$emit("communeValidated", commune);
    },
    resetCommune() {
      this.model.nom_commune = "";
      this.model.code_insee = "";
      this.model.code_postal = "";
    },
    hydrateModel(addresse) {
      this.model = { ...this.model, ...addresse };
    },
    adresseChange(adresse, viaAutosuggest = false) {
      if (!viaAutosuggest) {
        this.model.normalisee = false;
      }

      if (adresse.length <= 3) {
        this.$refs.dropdownAdresse.close();
        return;
      }
      if (this.debounceTime) {
        clearTimeout(this.debounceTime);
      }
      this.debounceTime = setTimeout(() => {
        this.model.normalisation_proposee = true;
        this.$emit("getSuggestionsAdresse", {
          code_insee: this.model.code_insee,
          adresse
        });
      }, 200);
    },
    adresseAutosuggestSelected(suggestion) {
      const isComplete =
        !suggestion.attributes.liste_numero ||
        (!!suggestion.attributes.numero &&
          suggestion.attributes.liste_numero
            .split(";")
            .includes(suggestion.attributes.numero));
      const adresse = suggestion.attributes.numero
        ? `${suggestion.attributes.numero} ${suggestion.attributes.voie}`
        : suggestion.attributes.voie;
      this.model.adresse = adresse;
      this.adresseKey = adresse + Math.random();
      if (isComplete) {
        this.model.normalisee = true;
        this.$emit("getComplementsAdresse", suggestion);
        // Nécessaire pour décompter un appel à DQE
        this.$emit("getCommunesByCodePostal", this.model.code_postal);
      } else {
        this.adresseChange(adresse, true);
        this.$refs.adresseInput.focus();
      }
    },
    complementAutosuggestSelected(complement) {
      this.model.complement_adresse = complement.attributes.complement;
      this.complementKey = complement.id + Math.random();
    },
    save() {
      if (this.checkValidation(this.v$)) {
        const isMaison = this.model.type_habitation !== "immeuble";
        if (isMaison) {
          this.model.etage = "0";
          this.model.ascenseur = false;
          this.model.appartement = "";
          this.model.digicode1 = "";
          this.model.digicode2 = "";
          this.model.interphone = false;
        }
        this.$emit("save", this.model);
      }
    },
    focusAdresseInput(e) {
      if (this.suggestionsAdresse.length > 0) {
        this.$refs.dropdownAdresse.open();
        this.$refs.dropdownComplement.close();
      } else {
        this.adresseChange(e.target.value);
      }
    },
    focusComplementInput() {
      if (this.suggestionsComplement.length > 0) {
        this.$refs.dropdownComplement.open();
      }
    }
  },
  watch: {
    adresse: {
      deep: true,
      handler(value) {
        if (value) {
          this.hydrateModel(value);
        }
      }
    },
    suggestionsAdresse: {
      deep: true,
      handler(value) {
        if (value && value.length > 0) {
          this.$refs.dropdownAdresse.open();
        }
      }
    },
    suggestionsComplement: {
      deep: true,
      handler(value) {
        if (value && value.length > 0) {
          this.$refs.dropdownComplement.open();
        }
      }
    }
  }
};
</script>

<template>
  <div>
    <ChoixCommune
      v-if="!model.nom_commune"
      :context="context"
      :check-eligibilite="checkEligibilite"
      @communeValidated="communeValidated"
      @cancel="$emit('cancel')"
    />
    <template v-else>
      <Alert
        class="c-address-lad-form__alert u-full-width"
        variant="grey-7"
      >
        <div class="u-pr-md u-pl-md u-p-sm@sm">
          <p class="u-font-700 u-font-15 u-mb-sm">
            {{ model.code_postal }} - {{ model.nom_commune }}
          </p>
          <Button
            link
            @click="resetCommune()"
          >
            <p class="u-font-14">
              Changer de commune
            </p>
          </Button>
        </div>
      </Alert>

      <Form
        ref="form"
        class="c-address-lad-form__wrapper u-pt-lg"
        :error="error"
        @submit.prevent="save()"
      >
        <FormFieldset>
          <p class="u-block u-font-13 u-mb-xs u-color-text-light">
            Civilité*
          </p>
          <FormGroup
            inline
            class="u-mb-sm"
            :state="getState(v$,'civilite', 'model')"
          >
            <FormRadio
              v-model="model.civilite"
              name="civilite"
              value="Mme"
              required
              class="u-mb-xs u-mr-lg"
            >
              Madame
            </FormRadio>
            <FormRadio
              v-model="model.civilite"
              name="civilite"
              value="M."
              class="u-mb-xs"
            >
              Monsieur
            </FormRadio>
            <template #message>
              <p>
                {{
                  getErrorMessage(
                    "civilite",
                    "Merci de renseigner votre civilité"
                  )
                }}
              </p>
            </template>
          </FormGroup>
          <FormGroup
            :state="getState(v$,'nom', 'model')"
            class="u-mb-sm"
          >
            <FormInput
              id="nom"
              v-model.trim="model.nom"
              type="text"
              :state="getState(v$,'nom', 'model')"
            >
              Nom*
            </FormInput>
            <template #message>
              <p>
                {{ getErrorMessage("nom", "Merci de renseigner votre nom") }}
              </p>
            </template>
          </FormGroup>
          <FormGroup
            id="prenom"
            :state="getState(v$,'prenom', 'model')"
            class=" u-mb-sm"
          >
            <FormInput
              v-model="model.prenom"
              type="text"
              :state="getState(v$,'prenom', 'model')"
            >
              Prénom*
            </FormInput>
            <template #message>
              <p>
                {{
                  getErrorMessage("prenom", "Merci de renseigner votre prénom")
                }}
              </p>
            </template>
          </FormGroup>
          <FormGroup
            class="u-mb-sm"
            :state="getState(v$,'tel', 'model')"
          >
            <FormInput
              v-model="model.tel"
              type="tel"
              size="lg"
              :state="getState(v$,'tel', 'model')"
              @blur="v$.model.tel.$touch()"
              @input="v$.model.tel.$reset()"
            >
              Téléphone*
            </FormInput>
            <template #message>
              <p>
                <template v-if="v$.model.tel.required.$invalid">
                  Merci de renseigner votre numéro de téléphone
                </template>
                <template v-else>
                  {{
                    getErrorMessage("tel", "Ce numéro de téléphone est invalide")
                  }}
                </template>
              </p>
            </template>
          </FormGroup>

          <FormGroup
            class="u-mb-md"
            :state="adresseAutosuggestIsLoading ? states.LOADING : getState(v$,'adresse', 'model')"
          >
            <DropDown
              ref="dropdownAdresse"
              scrollable
              :show-button="false"
              aria-label="Adresse"
              class="u-full-width u-block"
            >
              <template #content>
                <FormInput
                  ref="adresseInput"
                  :key="adresseKey"
                  v-model="model.adresse"
                  type="text"
                  :state="getState(v$,'adresse', 'model')"
                  role="combobox"
                  autocomplete="off"
                  aria-autocomplete="list"
                  @input="adresseChange"
                  @focus="focusAdresseInput"
                  @blur="v$.model.adresse.$touch()"
                >
                  Adresse*
                </FormInput>
              </template>
              <DropDownItem
                v-for="(suggestion, index) in suggestionsAdresse"
                :key="suggestion.id + suggestion.attributes.numero"
                :aria-pos="index + 1"
                :aria-total="suggestionsAdresse.length"
              >
                <Button
                  variant="text"
                  class="u-my-xs u-text-left"
                  @click="adresseAutosuggestSelected(suggestion)"
                >
                  {{ suggestion.attributes.numero }} {{ suggestion.attributes.voie }}
                </Button>
              </DropDownItem>
            </DropDown>
            <template #message>
              <p>
                {{
                  getErrorMessage("adresse", "Merci de renseigner votre adresse")
                }}
              </p>
            </template>
          </FormGroup>
          <FormGroup
            class="u-mr-xxxlg u-mr-xxs@sm u-mb-lg"
            :state="complementAutosuggestIsLoading ? states.LOADING : getState(v$,'complement_adresse', 'model')"
          >
            <DropDown
              ref="dropdownComplement"
              scrollable
              :show-button="false"
              aria-label="Complément"
              class="u-full-width u-block"
            >
              <template #content>
                <FormInput
                  ref="complementInput"
                  :key="complementKey"
                  v-model="model.complement_adresse"
                  type="text"
                  :state="getState(v$,'complement_adresse', 'model')"
                  role="combobox"
                  autocomplete="off"
                  aria-autocomplete="list"
                  @focus="focusComplementInput"
                >
                  Complément
                </FormInput>
              </template>

              <DropDownItem
                v-for="(complement, index) in suggestionsComplement"
                :key="complement.id"
                :aria-pos="index + 1"
                :aria-total="suggestionsComplement.length"
              >
                <Button
                  variant="text"
                  class="u-my-xs u-text-left"
                  @click="complementAutosuggestSelected(complement)"
                >
                  {{ complement.attributes.complement }}
                </Button>
              </DropDownItem>
            </DropDown>
            <template #message>
              <p>
                {{ getErrorMessage("complement_adresse") }}
              </p>
            </template>
          </FormGroup>
        </FormFieldset>

        <FormFieldset>
          <FormLegend
            title
            class="u-text-left u-mb-lg"
          >
            Ajouter des indications pour le livreur
          </FormLegend>

          <FormTip class="u-mb-sm">
            <p class="u-font-14 u-color-grey-1 u-block u-mb-md">
              Type d'habitation*
            </p>
            <FormGroup inline>
              <FormRadio
                v-model="model.type_habitation"
                name="habitation"
                value="maison"
                icon-name="house"
                icon-color="text"
                icon-size="sm"
                class="u-mr-lg u-mb-xs"
              >
                Maison
              </FormRadio>
              <FormRadio
                v-model="model.type_habitation"
                name="habitation"
                value="immeuble"
                class="u-mb-xs"
                icon-name="building"
                icon-color="text"
                icon-size="sm"
              >
                Immeuble
              </FormRadio>
            </FormGroup>
            <template v-if="model.type_habitation !== 'maison'">
              <FormGroup
                :state="getState(v$,'etage', 'model')"
                class="u-mb-md"
              >
                <FormSelect
                  v-model="model.etage"
                  title="Etage"
                  :options="etageOptions"
                  :state="getState(v$,'etage', 'model')"
                />
                <template #message>
                  <p>
                    {{
                      getErrorMessage(
                        "etage",
                        "Merci de renseigner l'étage dans lequel vous habitez"
                      )
                    }}
                  </p>
                </template>
              </FormGroup>
              <FormGroup
                inline
                :state="digicodeState"
                class="u-mb-md"
              >
                <FormInput
                  v-model="model.digicode1"
                  type="text"
                  :state="getState(v$,'digicode1', 'model')"
                >
                  Digicode 1
                </FormInput>
                <FormInput
                  v-model="model.digicode2"
                  type="text"
                  :state="getState(v$,'digicode2', 'model')"
                >
                  Digicode 2
                </FormInput>
                <template #message>
                  <p>
                    {{ digicodeError }}
                  </p>
                </template>
              </FormGroup>
              <p class="u-font-14 u-color-grey-1 u-block u-mt-sm u-mb-sm">
                Ascenseur*
              </p>
              <FormGroup inline>
                <FormRadio
                  v-model="model.ascenseur"
                  name="ascenseur"
                  :value="true"
                  class="u-mr-lg u-mb-xs"
                >
                  Oui
                </FormRadio>
                <FormRadio
                  v-model="model.ascenseur"
                  name="ascenseur"
                  :value="false"
                  class="u-mb-xs"
                >
                  Non
                </FormRadio>
              </FormGroup>
              <p class="u-font-14 u-color-grey-1 u-block u-mt-sm u-mb-sm">
                Interphone*
              </p>
              <FormGroup inline>
                <FormRadio
                  v-model="model.interphone"
                  name="interphone"
                  :value="true"
                  class="u-mr-lg u-mb-xs"
                >
                  Oui
                </FormRadio>
                <FormRadio
                  v-model="model.interphone"
                  name="interphone"
                  :value="false"
                  class="u-mb-xs"
                >
                  Non
                </FormRadio>
              </FormGroup>
            </template>
            <FormGroup
              class="u-mt-sm u-mb-md"
              :state="getState(v$,'info_livreur', 'model')"
            >
              <FormInput
                v-model="model.info_livreur"
                type="textarea"
                :state="getState(v$,'info_livreur', 'model')"
              >
                Informations pour le livreur
              </FormInput>
            </FormGroup>
            <FormGroup
              size="full"
              class="u-mb-md"
            >
              <p class="u-font-14 u-mb-sm">
                Merci de préciser les conditions d'accès à votre domicile
              </p>
              <div class="c-address-lad-form__access-checkbox-wrapper">
                <FormCheckbox
                  v-model="model.centre_ville"
                  class="c-address-lad-form__access-checkbox"
                >
                  <span class="u-font-15">Centre ville</span>
                </FormCheckbox>
                <FormCheckbox
                  v-model="model.voie_sans_issue"
                  class="c-address-lad-form__access-checkbox"
                >
                  <span class="u-font-15">Voie sans issue</span>
                </FormCheckbox>
                <FormCheckbox
                  v-model="model.sens_unique"
                  class="c-address-lad-form__access-checkbox"
                >
                  <span class="u-font-15">Sens unique</span>
                </FormCheckbox>
                <FormCheckbox
                  v-model="model.rue_pietonne"
                  class="c-address-lad-form__access-checkbox"
                >
                  <span class="u-font-15">Rue piétonne</span>
                </FormCheckbox>
                <FormCheckbox
                  v-model="model.access_semi_remorque"
                  class="c-address-lad-form__access-checkbox"
                >
                  <span class="u-font-15">Accès semi-remorque</span>
                </FormCheckbox>
                <FormCheckbox
                  v-model="model.residence"
                  class="c-address-lad-form__access-checkbox"
                >
                  <span class="u-font-15">Résidence</span>
                </FormCheckbox>
              </div>
            </FormGroup>
            <FormGroup
              class="u-mb-sm"
              :state="getState(v$,'libelle', 'model')"
            >
              <FormInput
                v-model="model.libelle"
                type="text"
                size="lg"
                :state="getState(v$,'libelle', 'model')"
              >
                Donner un nom à cette adresse*
              </FormInput>
              <template #message>
                <p>
                  {{
                    getErrorMessage(
                      "libelle",
                      "Merci de donner un nom à cette adresse"
                    )
                  }}
                </p>
              </template>
            </FormGroup>
            <template #tip>
              <div>
                <p class="u-font-13 u-mb-sm">
                  Les informations sur votre logement sont utilisées uniquement
                  pour aider le livreur à trouver votre domicile.
                </p>
              </div>
            </template>
            <FormGroup>
              <FormCheckbox v-model="model.defaut">
                <p class="u-color-grey-1 u-font-14">
                  Définir comme adresse de livraison par défaut
                </p>
              </FormCheckbox>
            </FormGroup>
          </FormTip>
        </FormFieldset>

        <div class="u-text-center">
          <Button
            variant="secondary"
            radius="full"
            class="u-mt-xlg u-mb-md"
            size="md"
            type="submit"
            value="J'enregistre cette adresse"
          />
        </div>
        <p class="u-font-14">
          * Les champs marqués par un astérisque sont obligatoires
        </p>
        <MentionsLegalesCompte class="u-mt-lg" />
      </Form>
    </template>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/components/address-lad-form.scss";
</style>
